// Pilot cohort ledger — pilot-cohort.md §4.

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type PilotCohortRow, type RosterRow } from "../lib/api";

const STATUSES = [
  "Invited", "Accepted", "Kicked off", "Active", "Active — at risk",
  "Completed", "Completed — extending into Q2", "Withdrew",
];

const STATUS_COLOR: Record<string, string> = {
  "Invited": "bg-slate-100 text-slate-800",
  "Accepted": "bg-blue-100 text-blue-900",
  "Kicked off": "bg-indigo-100 text-indigo-900",
  "Active": "bg-emerald-100 text-emerald-900",
  "Active — at risk": "bg-amber-100 text-amber-900",
  "Completed": "bg-green-100 text-green-900",
  "Completed — extending into Q2": "bg-purple-100 text-purple-900",
  "Withdrew": "bg-rose-100 text-rose-900",
};

const FUNCTIONS = ["Engineering","Sales","HR","Finance","Project Management","Operations","Other"];
const SENIORITIES = ["Lead","Sr IC","Jr"];
const SENTIMENTS = ["Enthusiast","Neutral","Skeptic"];

export function PilotCohort() {
  const qc = useQueryClient();
  const cohort = useQuery<PilotCohortRow[]>({ queryKey: ["pilot-cohort"], queryFn: api.pilotCohort.list });
  const roster = useQuery<RosterRow[]>({ queryKey: ["roster"], queryFn: api.roster.list });

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    pilot_id: "", manager_id: "",
    function: "Engineering", seniority: "Sr IC", sentiment: "Enthusiast",
    status: "Invited",
  });

  const create = useMutation({
    mutationFn: () => api.pilotCohort.create(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pilot-cohort"] });
      setAdding(false);
    },
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.pilotCohort.update(id, { status } as any),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pilot-cohort"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Pilot cohort</h1>
        <button
          className="px-3 py-1 rounded bg-blue-600 text-white text-sm"
          onClick={() => setAdding((s) => !s)}
        >{adding ? "Cancel" : "Add pilot"}</button>
      </div>

      {adding ? (
        <form
          className="border rounded p-3 bg-white space-y-2"
          onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
        >
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-600">Pilot</label>
              <select className="w-full border rounded px-2 py-1 text-sm"
                value={form.pilot_id}
                onChange={(e) => setForm({ ...form, pilot_id: e.target.value })}
                required
              >
                <option value="">— pick —</option>
                {(roster.data ?? []).filter((r) => !r.is_pilot).map((r) =>
                  <option key={r.id} value={r.id}>{r.name} ({r.function})</option>
                )}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600">Manager</label>
              <select className="w-full border rounded px-2 py-1 text-sm"
                value={form.manager_id}
                onChange={(e) => setForm({ ...form, manager_id: e.target.value })}
                required
              >
                <option value="">— pick —</option>
                {(roster.data ?? []).filter((r) => r.is_manager).map((r) =>
                  <option key={r.id} value={r.id}>{r.name} ({r.function})</option>
                )}
              </select>
            </div>
            <Select label="Function" value={form.function} onChange={(v) => setForm({ ...form, function: v })} options={FUNCTIONS} />
            <Select label="Seniority" value={form.seniority} onChange={(v) => setForm({ ...form, seniority: v })} options={SENIORITIES} />
            <Select label="Sentiment" value={form.sentiment} onChange={(v) => setForm({ ...form, sentiment: v })} options={SENTIMENTS} />
            <Select label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={STATUSES} />
          </div>
          {create.error ? <p className="text-red-700 text-sm">{(create.error as Error).message}</p> : null}
          <button className="px-3 py-1 rounded bg-blue-600 text-white text-sm">Add to cohort</button>
        </form>
      ) : null}

      <table className="w-full text-sm">
        <thead className="border-b border-slate-300 text-left">
          <tr>
            <th className="py-2">Pilot</th>
            <th>Function</th>
            <th>Manager</th>
            <th>Sentiment</th>
            <th>Status</th>
            <th>Retros</th>
          </tr>
        </thead>
        <tbody>
          {(cohort.data ?? []).map((c) => (
            <tr key={c.id} className="border-b border-slate-100">
              <td className="py-2">{c.pilot_name}</td>
              <td>{c.function}</td>
              <td className="text-slate-600">{c.manager_name}</td>
              <td>{c.sentiment}</td>
              <td>
                <select
                  className={`text-xs rounded px-1 py-0.5 ${STATUS_COLOR[c.status] ?? "bg-slate-100"}`}
                  value={c.status}
                  onChange={(e) => setStatus.mutate({ id: c.id, status: e.target.value })}
                >
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </td>
              <td className="space-x-2 text-xs">
                <Link className="text-blue-700 hover:underline" to={`/admin/retros/week-6/${c.pilot_id}`}>W6</Link>
                <Link className="text-blue-700 hover:underline" to={`/admin/retros/week-12/${c.pilot_id}`}>W12</Link>
              </td>
            </tr>
          ))}
          {(cohort.data ?? []).length === 0 ? (
            <tr><td colSpan={6} className="py-4 text-center text-slate-500">No pilots yet.</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <div>
      <label className="text-xs text-slate-600">{label}</label>
      <select className="w-full border rounded px-2 py-1 text-sm" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}
